import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Table,
  Collapse,
  Space,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
  TeamOutlined,
  BankOutlined,
  EnvironmentOutlined,
  BulbOutlined,
  ArrowUpOutlined,
  SearchOutlined,
  RiseOutlined,
} from "@ant-design/icons";
const { Title, Paragraph, Text } = Typography;

const COLORS = {
  teal: "#00A99E",
  charcoal: "#0A0A0A",
  amber: "#C97A2B",
  lightBg: "#F7F9F9",
  white: "#FFFFFF",
  grey: "#555555",
  border: "#E4E4E4",
};

const tagColor = (colour) => {
  switch ((colour || "").toLowerCase()) {
    case "green":
      return "success";
    case "red":
      return "error";
    case "amber":
      return "warning";
    case "blue":
      return "processing";
    case "grey":
    case "gray":
      return "default";
    default:
      return "default";
  }
};

const cardBackground = (colour) => {
  switch ((colour || "").toLowerCase()) {
    case "green":
      return "#F6FFED";
    case "lime":
      return "#FCFFE6";
    case "cyan":
      return "#E6FFFB";
    case "blue":
      return "#E6F4FF";
    case "geekblue":
      return "#F0F5FF";
    case "purple":
      return "#F9F0FF";
    case "magenta":
      return "#FFF0F6";
    case "pink":
      return "#FFF0F6";
    case "red":
      return "#FFF2F0";
    case "volcano":
      return "#FFF2E8";
    case "orange":
      return "#FFF7E6";
    case "gold":
      return "#FFFBE6";
    case "yellow":
      return "#FEFFE6";
    case "amber":
      return "#FFFBE6";
    case "grey":
    case "gray":
      return "#FAFAFA";
    default:
      return COLORS.white;
  }
};

const getMetricIcon = (label) => {
  const text = (label || "").toLowerCase();

  if (text.includes("application")) {
    return <FileTextOutlined />;
  }

  if (text.includes("quality")) {
    return <CheckCircleOutlined />;
  }

  if (
    text.includes("cost") ||
    text.includes("cpa")
  ) {
    return <BankOutlined />;
  }

  if (text.includes("occupation")) {
    return <TeamOutlined />;
  }

  if (
    text.includes("geograph") ||
    text.includes("location") ||
    text.includes("city")
  ) {
    return <EnvironmentOutlined />;
  }

  return <BarChartOutlined />;
};

const StatusDot = ({ colour = "teal" }) => {
  const color =
    colour === "amber"
      ? COLORS.amber
      : colour === "grey"
      ? "#8C8C8C"
      : COLORS.teal;

  return (
    <span
      className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 mt-2"
      style={{ backgroundColor: color }}
    />
  );
};

const SectionHeader = ({
  emoji,
  title,
}) => (
  <div className="mb-6">
    <h2
      className="text-xl sm:text-2xl font-bold mb-2"
      style={{ color: COLORS.teal }}
    >
      {emoji} {title}
    </h2>

    <div
      className="h-1 w-20 rounded-full"
      style={{ backgroundColor: COLORS.teal }}
    />
  </div>
);

const HeroStats = ({ stats = [] }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
      {stats.map((item, index) => (
        <div
          key={index}
          className="
            rounded-xl
            px-5 py-4
            min-h-[110px]
            border
            transition-shadow
            hover:shadow-md
          "
          style={{
            background: "rgba(0,169,158,0.12)",
            borderColor: COLORS.teal,
          }}
        >
          <div
            className="text-2xl sm:text-3xl font-bold break-words"
            style={{ color: COLORS.teal }}
          >
            {item?.value ?? "—"}
          </div>

          <div className="text-sm text-gray-300 mt-1 break-words">
            {item?.label}
          </div>
        </div>
      ))}
    </div>
  );
};

const Hero = ({ report }) => {
  const meta = report?.meta || {};

  return (
    <div
      className="
        rounded
        overflow-hidden
        text-white
        shadow-lg
        border-b-[6px]
      "
      style={{
        background:
          "linear-gradient(135deg, #0A0A0A 0%, #1C1C1C 100%)",
        borderBottomColor: COLORS.teal,
      }}
    >
      <div className="px-5 sm:px-8 lg:px-10 py-8 sm:py-10">
        <div className="flex flex-wrap items-center justify-center gap-4">
					<div className="text-center">
						<h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 break-words">
							Executive Summary
							{meta?.buyer_name
								? ` — ${meta.buyer_name}`
								: ""}
						</h1>

						<div
							className="font-semibold text-base sm:text-lg"
							style={{ color: COLORS.teal }}
						>
							{meta?.date_range_display || "—"}
						</div>
					</div>
        </div>

        <HeroStats stats={report?.hero_stats} />
      </div>
    </div>
  );
};

const OneMinuteRead = ({ data }) => {
  if (!data) return null;

  return (
		<section className="w-full max-w-[940px] mx-auto">
      <SectionHeader
        emoji="1️⃣"
        title="One-Minute Read"
      />

      {/* Headline */}
      {data?.headline && (
        <div
          className="
            bg-white
            rounded-lg
            px-6 py-5
            mb-5
            shadow-sm
            border-l-[5px]
          "
          style={{
            borderLeftColor: COLORS.teal,
          }}
        >
          <div className="flex items-start gap-3">
            <span className="text-xl">📊</span>

            <div>
              <h3
                className="font-bold text-lg mb-2"
                style={{ color: COLORS.charcoal }}
              >
                The Headline
              </h3>

              <Paragraph className="!mb-0 text-gray-700 leading-7">
                {data.headline}
              </Paragraph>
            </div>
          </div>
        </div>
      )}

			<Row gutter={[20, 20]}>
				{/* What's Working */}
				<Col xs={24}>
					<div
						className="
        bg-white
        rounded-lg
        px-6 py-5
        h-full
        shadow-sm
        border-l-[5px]
      "
						style={{
							borderLeftColor: COLORS.teal,
						}}
					>
						<h3
							className="font-bold text-lg mb-4"
							style={{ color: COLORS.charcoal }}
						>
							✅ What's Working
						</h3>

						<Space
							direction="vertical"
							className="w-full"
						>
							{(data?.whats_working || []).map(
								(item, index) => (
									<div
										key={index}
										className="flex items-start gap-2"
									>
										<StatusDot colour="teal" />

										<Text className="leading-6">
											{item}
										</Text>
									</div>
								)
							)}
						</Space>
					</div>
				</Col>

				{/* Room to Improve */}
				<Col xs={24}>
					<div
						className="
        bg-white
        rounded-lg
        px-6 py-5
        h-full
        shadow-sm
        border-l-[5px]
      "
						style={{
							borderLeftColor: COLORS.amber,
						}}
					>
						<h3
							className="font-bold text-lg mb-4"
							style={{ color: COLORS.charcoal }}
						>
							🔍 Room to Improve
						</h3>

						<Space
							direction="vertical"
							className="w-full"
						>
							{(data?.room_to_improve || []).map(
								(item, index) => (
									<div
										key={index}
										className="flex items-start gap-2"
									>
										<StatusDot colour="amber" />

										<Text className="leading-6">
											{item}
										</Text>
									</div>
								)
							)}
						</Space>
					</div>
				</Col>
			</Row>

      {/* Opportunity */}
      {data?.opportunity_statement && (
        <div
          className="
            rounded-lg
            px-6 py-5
            mt-5
            text-white
          "
          style={{
            backgroundColor: COLORS.charcoal,
          }}
        >
          <div
            className="font-bold uppercase text-sm tracking-wide mb-2"
            style={{ color: COLORS.teal }}
          >
            🎯 Opportunity
          </div>

          <Paragraph className="!mb-0 !text-white leading-7">
            {data.opportunity_statement}
          </Paragraph>
        </div>
      )}
    </section>
  );
};

const FindingStats = ({ stats = [] }) => {
  if (!stats?.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 my-5">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="rounded-lg px-4 py-4 font-semibold"
          style={{
            backgroundColor:
              stat?.colour
                ? cardBackground(stat.colour)
                : COLORS.lightBg,
          }}
        >
          <div className="text-sm text-gray-600 mb-1">
            {stat?.label}
          </div>

          <div className="text-lg break-words">
            {stat?.value ?? "—"}
          </div>
        </div>
      ))}
    </div>
  );
};

const Findings = ({ findings = [] }) => {
  if (!findings?.length) return null;

  return (
		<section className="w-full max-w-[940px] mx-auto">
      <SectionHeader
        emoji="2️⃣"
        title="Four Key Findings"
      />

      <Space
        direction="vertical"
        size={24}
        className="w-full"
      >
        {findings.map((finding, index) => (
          <div
            key={index}
            className="
              bg-white
              rounded-xl
              px-5 sm:px-7
              py-6
              shadow-sm
              border-t-4
            "
            style={{
              borderTopColor: COLORS.teal,
            }}
          >
            {/* Header */}
            <div className="flex items-start gap-3 mb-1">
              <span
                className="
                  inline-flex
                  items-center
                  justify-center
                  w-9 h-9
                  rounded-full
                  text-white
                  font-bold
                  text-base
                  flex-shrink-0
                "
                style={{
                  backgroundColor: COLORS.teal,
                }}
              >
                {finding?.number ?? index + 1}
              </span>

              <h3
                className="
                  text-lg sm:text-xl
                  font-bold
                  m-0
                  pt-1
                  break-words
                "
                style={{
                  color: COLORS.charcoal,
                }}
              >
                {finding?.title}
              </h3>
            </div>

            {/* Subheading */}
            {finding?.subheading && (
              <div
                className="
                  italic
                  text-sm sm:text-base
                  mb-4
                  ml-0 sm:ml-[51px]
                "
                style={{
                  color: COLORS.grey,
                }}
              >
                {finding.subheading}
              </div>
            )}

            <div
              className="h-0.5 my-4 rounded"
              style={{
                backgroundColor: COLORS.border,
              }}
            />

            {/* Stats */}
            <FindingStats
              stats={finding?.stats}
            />

            {/* Insight */}
            {finding?.insight && (
              <div className="mt-5">
                <div
                  className="
                    uppercase
                    text-sm
                    tracking-wide
                    font-bold
                    mb-2
                  "
                  style={{
                    color: COLORS.teal,
                  }}
                >
                  The Insight
                </div>

                <Paragraph className="text-gray-700 leading-7 !mb-0">
                  {finding.insight}
                </Paragraph>
              </div>
            )}

            {/* What this means */}
            {finding?.what_this_means && (
              <div className="mt-5">
                <div
                  className="
                    uppercase
                    text-sm
                    tracking-wide
                    font-bold
                    mb-2
                  "
                  style={{
                    color: COLORS.teal,
                  }}
                >
                  What This Means
                </div>

                <Paragraph className="text-gray-700 leading-7 !mb-0">
                  {finding.what_this_means}
                </Paragraph>
              </div>
            )}

            {/* What this opens up */}
            {finding?.what_this_opens_up && (
              <div className="mt-5">
                <div
                  className="
                    uppercase
                    text-sm
                    tracking-wide
                    font-bold
                    mb-2
                  "
                  style={{
                    color: COLORS.teal,
                  }}
                >
                  What This Opens Up
                </div>

                <Paragraph className="text-gray-700 leading-7 !mb-0">
                  {finding.what_this_opens_up}
                </Paragraph>
              </div>
            )}

            {/* Recommendation */}
            {(finding?.recommendation_label ||
              finding?.recommendation) && (
              <div
                className="
                  rounded-lg
                  px-5 py-5
                  mt-5
                  text-white
                "
                style={{
                  backgroundColor: COLORS.charcoal,
                }}
              >
                <div
                  className="
                    uppercase
                    text-sm
                    tracking-wide
                    font-bold
                    mb-2
                  "
                  style={{
                    color: COLORS.teal,
                  }}
                >
                  🎯{" "}
                  {finding?.recommendation_label ||
                    "Recommendation"}
                </div>

                {finding?.recommendation && (
                  <Paragraph className="!mb-0 !text-white leading-7">
                    {finding.recommendation}
                  </Paragraph>
                )}
              </div>
            )}
          </div>
        ))}
      </Space>
    </section>
  );
};

const ExecutiveSnapshot = ({ snapshot }) => {
  if (!snapshot) return null;

  const tableColumns =
    snapshot?.table?.headers?.map(
      (header, index) => ({
        title: header,
        dataIndex: `col${ index } `,
        key: `col${ index } `,
        render: (value) => value ?? "—",
      })
    ) || [];

  const tableData =
    snapshot?.table?.rows?.map(
      (row, rowIndex) => {
        const obj = {
          key: rowIndex,
        };

        row.forEach((cell, cellIndex) => {
          obj[`col${ cellIndex } `] =
            cell;
        });

        return obj;
      }
    ) || [];

  const hasTable =
    snapshot?.table &&
    Array.isArray(snapshot.table.headers) &&
    snapshot.table.headers.length > 0 &&
    Array.isArray(snapshot.table.rows);

  return (
    <section className="w-full max-w-[940px] mx-auto">
      <SectionHeader
        emoji="3️⃣"
        title="Executive Snapshot"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Portfolio Metrics */}
        {snapshot?.portfolio_metrics?.length > 0 && (
          <div
            className="
              bg-white
              rounded-xl
              p-5
              shadow-sm
              border-t-4
            "
            style={{
              borderTopColor: COLORS.teal,
            }}
          >
            <h3
              className="font-bold text-lg mb-4"
              style={{
                color: COLORS.charcoal,
              }}
            >
              🎯 Portfolio Metrics
            </h3>

            <div className="space-y-3">
              {snapshot.portfolio_metrics.map(
                (metric, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between gap-4"
                  >
                    <Text className="font-medium">
                      {metric?.label}
                    </Text>

                    <Text
                      strong
                      className="text-right break-words"
                    >
                      {metric?.value ?? "—"}
                    </Text>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        {/* Paid vs Free */}
        {snapshot?.paid_vs_free?.length > 0 && (
          <div
            className="
              bg-white
              rounded-xl
              p-5
              shadow-sm
              border-t-4
            "
            style={{
              borderTopColor: COLORS.teal,
            }}
          >
            <h3
              className="font-bold text-lg mb-4"
              style={{
                color: COLORS.charcoal,
              }}
            >
              ⚖️ Paid vs Free
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {snapshot.paid_vs_free.map(
                (item, index) => (
                  <div
                    key={index}
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor:
                        COLORS.lightBg,
                    }}
                  >
                    <div className="text-sm text-gray-600 mb-1">
                      {item?.label}
                    </div>

                    <div className="font-bold text-lg break-words">
                      {item?.value ?? "—"}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        {/* Top Occupations */}
        {snapshot?.top_occupations?.length > 0 && (
          <div
            className="
              bg-white
              rounded-xl
              p-5
              shadow-sm
              border-t-4
            "
            style={{
              borderTopColor: COLORS.teal,
            }}
          >
            <h3
              className="font-bold text-lg mb-4"
              style={{
                color: COLORS.charcoal,
              }}
            >
              🏆 Top Occupations
            </h3>
            <div className="space-y-3">
              {snapshot.top_occupations.map(
                (item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3"
                  >
                    <span
                      className="
                        flex
                        items-center
                        justify-center
                        w-7 h-7
                        rounded-full
                        text-white
                        font-bold
                        text-sm
                        flex-shrink-0
                      "
                      style={{
                        backgroundColor:
                          COLORS.teal,
                      }}
                    >
                      {item?.rank ??
                        index + 1}
                    </span>

                    <div className="min-w-0">
                      <Text
                        strong
                        className="block break-words"
                      >
                        {item?.occupation}
                      </Text>

                      {item?.note && (
                        <Text
                          type="secondary"
                          className="leading-5"
                        >
                          {item.note}
                        </Text>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        {/* Areas to Explore */}
        {snapshot?.areas_to_explore?.length > 0 && (
          <div
            className="
              bg-white
              rounded-xl
              p-5
              shadow-sm
              border-t-4
            "
            style={{
              borderTopColor: COLORS.amber,
            }}
          >
            <h3
              className="font-bold text-lg mb-4"
              style={{
                color: COLORS.charcoal,
              }}
            >
              🔍 Areas to Explore
            </h3>

            <Space
              direction="vertical"
              className="w-full"
            >
              {snapshot.areas_to_explore.map(
                (item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2"
                  >
                    <StatusDot colour="amber" />

                    <Text className="leading-6">
                      {item}
                    </Text>
                  </div>
                )
              )}
            </Space>
          </div>
        )}
      </div>
      {/* Occupation Highlights */}
      {snapshot?.occupation_highlights && (
        <div
          className="
            grid
            grid-cols-1
            md:grid-cols-2
            gap-5
            mt-5
          "
        >
          {snapshot?.occupation_highlights
            ?.strongest && (
            <div
              className="
                bg-white
                rounded-xl
                p-5
                shadow-sm
                border-t-4
              "
              style={{
                borderTopColor:
                  COLORS.teal,
              }}
            >
              <h3
                className="font-bold text-lg mb-3"
                style={{
                  color:
                    COLORS.charcoal,
                }}
              >
                💪 Strongest Occupation
              </h3>
              <Text
                strong
                className="block text-base mb-1"
              >
                {
                  snapshot
                    .occupation_highlights
                    .strongest
                    .occupation
                }
              </Text>
              <Text type="secondary">
                {
                  snapshot
                    .occupation_highlights
                    .strongest
                    .note
                }
              </Text>
            </div>
          )}
          {snapshot?.occupation_highlights
            ?.weakest && (
            <div
              className="
                bg-white
                rounded-xl
                p-5
                shadow-sm
                border-t-4
              "
              style={{
                borderTopColor:
                  COLORS.amber,
              }}
            >
              <h3
                className="font-bold text-lg mb-3"
                style={{
                  color:
                    COLORS.charcoal,
                }}
              >
                🔎 Occupation to Explore
              </h3>
              <Text
                strong
                className="block text-base mb-1"
              >
                {
                  snapshot
                    .occupation_highlights
                    .weakest
                    .occupation
                }
              </Text>
              <Text type="secondary">
                {
                  snapshot
                    .occupation_highlights
                    .weakest
                    .note
                }
              </Text>
            </div>
          )}
        </div>
      )}
      {/* Optional Comparison Table */}
      {hasTable && (
        <div
          className="
            bg-white
            rounded-xl
            p-5
            mt-5
            shadow-sm
            border-t-4
            overflow-hidden
          "
          style={{
            borderTopColor: COLORS.teal,
          }}
        >
          <h3
            className="font-bold text-lg mb-4"
            style={{
              color: COLORS.charcoal,
            }}
          >
            📊 Comparison
          </h3>
          <Table
            bordered
            size="middle"
            columns={tableColumns}
            dataSource={tableData}
            pagination={false}
            scroll={{
              x: "max-content",
            }}
          />
        </div>
      )}
    </section>
  );
};

const WhatsWorking = ({ items = [] }) => {
  if (!items?.length) return null;

  return (
		<section className="w-full max-w-[940px] mx-auto">
      <SectionHeader
        emoji="4️⃣"
        title="What's Working — Build on This"
      />
      <Space
        direction="vertical"
        size={18}
        className="w-full"
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="
              bg-white
              rounded-lg
              px-5 sm:px-6
              py-5
              shadow-sm
              border-l-[5px]
            "
            style={{
              borderLeftColor: COLORS.teal,
            }}
          >
            <div className="flex items-start gap-3">
              <CheckCircleOutlined
                className="text-lg mt-1 flex-shrink-0"
                style={{
                  color: COLORS.teal,
                }}
              />
              <div className="min-w-0">
                <h3
                  className="font-bold text-base sm:text-lg mb-2 break-words"
                  style={{
                    color:
                      COLORS.charcoal,
                  }}
                >
                  {item?.label}
                </h3>
                <Paragraph className="!mb-0 text-gray-700 leading-7">
                  {item?.detail}
                </Paragraph>
              </div>
            </div>
          </div>
        ))}
      </Space>
    </section>
  );
};

const WhyNow = ({ data }) => {
  if (!data) return null;

  return (
    <section className="w-full max-w-[940px] mx-auto pb-2">
      <SectionHeader
        emoji="5️⃣"
        title="Why Now — Fine-Tuning, Not Overhaul"
      />
      <div
        className="
          bg-white
          rounded-xl
          px-5 sm:px-8
          py-6
          shadow-sm
          border-t-[5px]
        "
        style={{
          borderTopColor: COLORS.teal,
        }}
      >
        {/* Opening statement */}
        {data?.opening_statement && (
          <Paragraph className="text-base leading-7 text-gray-700">
            {data.opening_statement}
          </Paragraph>
        )}
        {/* Recommendations */}
        {data?.recommendations_recap?.length > 0 && (
          <div className="mt-6">
            <h3
              className="font-bold text-lg mb-4"
              style={{
                color:
                  COLORS.charcoal,
              }}
            >
              🎯 Recommendations Recap
            </h3>
            <ul className="pl-5 space-y-3">
              {data.recommendations_recap.map(
                (item, index) => (
                  <li
                    key={index}
                    className="
                      leading-6
                      font-medium
                      text-gray-700
                    "
                  >
                    {item}
                  </li>
                )
              )}
            </ul>
          </div>
        )}
        {/* Expected Outcome */}
        {data?.expected_outcome && (
          <div
            className="
              rounded-lg
              px-5 py-4
              mt-6
              text-white
              text-center
              font-bold
              text-base
              sm:text-lg
            "
            style={{
              backgroundColor:
                COLORS.teal,
            }}
          >
            ✨ {data.expected_outcome}
          </div>
        )}
      </div>
    </section>
  );
};

const AnalysisSummary = ({ report }) => {
  if (!report) return null;

  return (
    <div
      className="
        w-full
        max-w-full
        overflow-x-hidden
        space-y-8
      "
			style={{ backgroundColor: COLORS.lightBg, color: COLORS.charcoal, }}
    >
      {/* Hero */}
      <Hero report={report} />
      {/* One Minute Read */}
      <OneMinuteRead
        data={report?.one_minute_read}
      />
      {/* Findings */}
      <Findings
        findings={report?.findings}
      />
      {/* Executive Snapshot */}
      <ExecutiveSnapshot
        snapshot={
          report?.executive_snapshot
        }
      />
      {/* What's Working */}
      <WhatsWorking
        items={report?.whats_working}
      />
      {/* Why Now */}
      <WhyNow
        data={report?.why_now}
      />
    </div>
  );
};
export default AnalysisSummary;
